import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { media } from '../ui-components/styles';

import curved80 from '../assets/underline/curved/curved-80.svg';
import curved100 from '../assets/underline/curved/curved-100.svg';
import curved120 from '../assets/underline/curved/curved-120.svg';
import curved140 from '../assets/underline/curved/curved-140.svg';
import curved160 from '../assets/underline/curved/curved-160.svg';
import curved180 from '../assets/underline/curved/curved-180.svg';
import curved200 from '../assets/underline/curved/curved-200.svg';
import curved220 from '../assets/underline/curved/curved-220.svg';
import curved240 from '../assets/underline/curved/curved-240.svg';
import curved260 from '../assets/underline/curved/curved-260.svg';
import curved280 from '../assets/underline/curved/curved-280.svg';
import curved300 from '../assets/underline/curved/curved-300.svg';
import curved320 from '../assets/underline/curved/curved-320.svg';
import curved340 from '../assets/underline/curved/curved-340.svg';
import curved360 from '../assets/underline/curved/curved-360.svg';
import curved380 from '../assets/underline/curved/curved-380.svg';
import curved400 from '../assets/underline/curved/curved-400.svg';
import curved420 from '../assets/underline/curved/curved-420.svg';
import curved440 from '../assets/underline/curved/curved-440.svg';
import curved460 from '../assets/underline/curved/curved-460.svg';
import curved480 from '../assets/underline/curved/curved-480.svg';
import curved500 from '../assets/underline/curved/curved-500.svg';
import curved520 from '../assets/underline/curved/curved-520.svg';

import { colors } from '../ui-components/styles';

const propTypes = {
  phrase: PropTypes.string.isRequired,
  underlinedText: PropTypes.string.isRequired,
  underlineType: PropTypes.string,
  underlineWidth: PropTypes.number.isRequired,
  bottom: PropTypes.number
};

/**
 * Finds the correct sized curved underline SVG and returns it.
 * 
 * @function getCurvedUnderline
 * 
 * @param {number} underlineWidth - set the width of the SVG underline (account for longer text)
 * @param {number} bottom - allows fine-tuning of the gap above the underline
 *
 * @returns {JSX}
 */
const getCurvedUnderline = (underlineWidth, bottom) => {
  let underlineSvg = null;

  if (underlineWidth <= 80) {
    underlineSvg = curved80;
  } else if (underlineWidth > 80 && underlineWidth <= 100) {
    underlineSvg = curved100;
  } else if (underlineWidth > 100 && underlineWidth <= 120) {
    underlineSvg = curved120;
  } else if (underlineWidth > 120 && underlineWidth <= 140) {
    underlineSvg = curved140;
  } else if (underlineWidth > 140 && underlineWidth <= 160) {
    underlineSvg = curved160;
  } else if (underlineWidth > 160 && underlineWidth <= 180) {
    underlineSvg = curved180;
  } else if (underlineWidth > 180 && underlineWidth <= 200) {
    underlineSvg = curved200;
  } else if (underlineWidth > 200 && underlineWidth <= 220) {
    underlineSvg = curved220;
  } else if (underlineWidth > 220 && underlineWidth <= 240) {
    underlineSvg = curved240;
  } else if (underlineWidth > 240 && underlineWidth <= 260) {
    underlineSvg = curved260;
  } else if (underlineWidth > 260 && underlineWidth <= 280) {
    underlineSvg = curved280;
  } else if (underlineWidth > 280 && underlineWidth <= 300) {
    underlineSvg = curved300;
  } else if (underlineWidth > 300 && underlineWidth <= 320) {
    underlineSvg = curved320;
  } else if (underlineWidth > 320 && underlineWidth <= 340) {
    underlineSvg = curved340;
  } else if (underlineWidth > 340 && underlineWidth <= 360) {
    underlineSvg = curved360;
  } else if (underlineWidth > 360 && underlineWidth <= 380) {
    underlineSvg = curved380;
  } else if (underlineWidth > 380 && underlineWidth <= 400) {
    underlineSvg = curved400;
  } else if (underlineWidth > 400 && underlineWidth <= 420) {
    underlineSvg = curved420;
  } else if (underlineWidth > 420 && underlineWidth <= 440) {
    underlineSvg = curved440;
  } else if (underlineWidth > 440 && underlineWidth <= 460) {
    underlineSvg = curved460;
  } else if (underlineWidth > 460 && underlineWidth <= 480) {
    underlineSvg = curved480;
  } else if (underlineWidth > 480 && underlineWidth <= 500) {
    underlineSvg = curved500;
  } else {
    underlineSvg = curved520;
  }

  return <UnderlineCurved src={underlineSvg} alt="Red Curved Underline" bottom={bottom} />;
};

/**
 * Underline component that adds an underline the final word of a given phrase
 *
 * @component Underline
 * 
 * @param {object} props
 * 
 * @prop {string} phrase - the phrase to be underlined
 * @prop {string} underlinedText - the text in the phrase that should be underlined
 * @prop {string} underlineType - the type of underline, curved by default
 * @prop {number} underlineWidth - set the width of the SVG underline (account for longer text)
 * @prop {number} bottom - allows fine-tuning of the gap above the underline
 */
const Underline = props => {
  const { phrase = '', underlinedText = '', underlineType = 'curved', underlineWidth, bottom } = props;

  const splitPhrase = phrase.split(new RegExp(underlinedText, 'g'));

  const underlines = {
    curved: getCurvedUnderline(underlineWidth, bottom)
  };
  const underline = underlines[underlineType];

  if (underlineType === 'solid') {
    return (
      <>
        {splitPhrase[0]}
        <UnderlineSolid>
          <UnderlinedWord>{underlinedText}</UnderlinedWord>
        </UnderlineSolid>
        {splitPhrase[1]}
      </>
    );
  } else {
    return (
      <>
        {splitPhrase[0]}
        <UnderlineWrapper>
          <UnderlinedWord>{underlinedText}</UnderlinedWord>
          {underline}
        </UnderlineWrapper>
        {splitPhrase[1]}
      </>
    );
  }
};

Underline.propTypes = propTypes;

export default Underline;

const UnderlineWrapper = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
`;

const UnderlinedWord = styled.span`
  position: relative;
  z-index: 1;
`;

const UnderlineCurved = styled.img`
  margin: 0;
  position: absolute;
  --bottom: ${({ bottom }) => (bottom ? `${bottom}px` : '0px')};
  top: calc(80% + var(--bottom));
  pointer-events: none;
  user-select: none;

  @media screen and (max-width: ${media.mediumBottom}) {
    top: calc(70% + var(--bottom));
  }
`;

const UnderlineSolid = styled.span`
  border-bottom: 6px solid ${colors.crederaRed};
`;
